import type { ApiClient } from '../client';
import type {
  ApiResponse,
  DashboardActivityItem,
  DashboardCollectionDetails,
  DashboardFeeDefaulters,
  DashboardFeeDefaultersQuery,
  DashboardPunctualPayers,
  DashboardPunctualPayersQuery,
  DashboardDetailPanelMeta,
  DashboardLayoutCatalogResponse,
  DashboardLayoutResponse,
  DashboardStats,
  DashboardVisitorReport,
  DashboardSecurityOverview,
  SaveDashboardPreferencesRequest,
  SaveTenantDashboardLayoutRequest,
  TenantDashboardLayoutCatalogResponse,
} from '../types';

export function bindDashboard(client: ApiClient) {
  return {
    async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
      return client.request<DashboardStats>('/dashboard/stats');
    },

    async getRecentActivities(): Promise<ApiResponse<DashboardActivityItem[]>> {
      return client.request<DashboardActivityItem[]>('/dashboard/activities');
    },

    async getDashboardLayout(): Promise<ApiResponse<DashboardLayoutResponse>> {
      return client.request<DashboardLayoutResponse>('/dashboard/layout');
    },

    async getDashboardLayoutCatalog(): Promise<ApiResponse<DashboardLayoutCatalogResponse>> {
      return client.request<DashboardLayoutCatalogResponse>('/dashboard/layout/catalog');
    },

    async saveDashboardPreferences(
      body: SaveDashboardPreferencesRequest
    ): Promise<ApiResponse<DashboardLayoutResponse>> {
      return client.request<DashboardLayoutResponse>('/dashboard/layout/preferences', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async resetDashboardPreferences(): Promise<ApiResponse<DashboardLayoutResponse>> {
      return client.request<DashboardLayoutResponse>('/dashboard/layout/preferences', {
        method: 'DELETE',
      });
    },

    async getDashboardDetailPanels(): Promise<ApiResponse<DashboardDetailPanelMeta[]>> {
      return client.request<DashboardDetailPanelMeta[]>('/dashboard/detail-panels');
    },

    async getDashboardCollectionDetails(
      params?: {
        month?: string;
        year?: string;
        fromDate?: string;
        toDate?: string;
      }
    ): Promise<ApiResponse<DashboardCollectionDetails>> {
      const qs = new URLSearchParams();
      if (params?.month?.trim()) qs.set('month', params.month.trim());
      if (params?.year?.trim()) qs.set('year', params.year.trim());
      if (params?.fromDate?.trim()) qs.set('from_date', params.fromDate.trim());
      if (params?.toDate?.trim()) qs.set('to_date', params.toDate.trim());
      const query = qs.toString();
      return client.request<DashboardCollectionDetails>(
        query ? `/dashboard/collection-details?${query}` : '/dashboard/collection-details'
      );
    },

    async getDashboardPunctualPayers(
      params?: DashboardPunctualPayersQuery
    ): Promise<ApiResponse<DashboardPunctualPayers>> {
      const qs = new URLSearchParams();
      if (params?.fromDate?.trim()) qs.set('from_date', params.fromDate.trim());
      if (params?.toDate?.trim()) qs.set('to_date', params.toDate.trim());
      if (params?.punctualDay != null && params.punctualDay >= 1 && params.punctualDay <= 31) {
        qs.set('punctual_day', String(params.punctualDay));
      }
      if (params?.classId?.trim() && params.classId !== 'all') {
        qs.set('class_id', params.classId.trim());
      }
      if (params?.sectionId?.trim() && params.sectionId !== 'all') {
        qs.set('section_id', params.sectionId.trim());
      }
      if (params?.paymentFilter?.trim() && params.paymentFilter !== 'all') {
        qs.set('payment_filter', params.paymentFilter.trim());
      }
      if (params?.onlyPunctual === true) qs.set('only_punctual', 'true');
      if (params?.onlyPunctual === false) qs.set('only_punctual', 'false');
      if (params?.includeStudents === false) qs.set('include_students', 'false');
      if (params?.page != null) qs.set('page', String(params.page));
      if (params?.limit != null) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return client.request<DashboardPunctualPayers>(
        query ? `/dashboard/punctual-payers?${query}` : '/dashboard/punctual-payers'
      );
    },

    async getDashboardFeeDefaulters(
      params?: DashboardFeeDefaultersQuery
    ): Promise<ApiResponse<DashboardFeeDefaulters>> {
      const qs = new URLSearchParams();
      if (params?.fromDate?.trim()) qs.set('from_date', params.fromDate.trim());
      if (params?.toDate?.trim()) qs.set('to_date', params.toDate.trim());
      if (params?.classId?.trim() && params.classId !== 'all') {
        qs.set('class_id', params.classId.trim());
      }
      if (params?.sectionId?.trim() && params.sectionId !== 'all') {
        qs.set('section_id', params.sectionId.trim());
      }
      if (params?.studentId?.trim() && params.studentId !== 'all') {
        qs.set('student_id', params.studentId.trim());
      }
      if (params?.top != null && params.top > 0) qs.set('top', String(params.top));
      if (params?.includeStudents === false) qs.set('include_students', 'false');
      if (params?.page != null) qs.set('page', String(params.page));
      if (params?.limit != null) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return client.request<DashboardFeeDefaulters>(
        query ? `/dashboard/fee-defaulters?${query}` : '/dashboard/fee-defaulters'
      );
    },

    async getDashboardVisitorReport(params?: {
      from_date?: string;
      to_date?: string;
      visitor_type?: string;
      status?: string;
    }): Promise<ApiResponse<DashboardVisitorReport>> {
      const qs = new URLSearchParams();
      if (params?.from_date?.trim()) qs.set('from_date', params.from_date.trim());
      if (params?.to_date?.trim()) qs.set('to_date', params.to_date.trim());
      if (params?.visitor_type?.trim() && params.visitor_type !== 'all') {
        qs.set('visitor_type', params.visitor_type.trim());
      }
      if (params?.status?.trim() && params.status !== 'all') {
        qs.set('status', params.status.trim());
      }
      const query = qs.toString();
      const path = query ? `/dashboard/visitor-report?${query}` : '/dashboard/visitor-report';
      return client.request<DashboardVisitorReport>(path);
    },

    async getDashboardSecurityOverview(params?: {
      period?: 'day' | 'week' | 'month' | 'year' | string;
      from_date?: string;
      to_date?: string;
      year?: string;
    }): Promise<ApiResponse<DashboardSecurityOverview>> {
      const qs = new URLSearchParams();
      if (params?.period?.trim()) qs.set('period', params.period.trim());
      if (params?.from_date?.trim()) qs.set('from_date', params.from_date.trim());
      if (params?.to_date?.trim()) qs.set('to_date', params.to_date.trim());
      if (params?.year?.trim()) qs.set('year', params.year.trim());
      const query = qs.toString();
      const path = query ? `/dashboard/security-overview?${query}` : '/dashboard/security-overview';
      return client.request<DashboardSecurityOverview>(path);
    },

    async getTenantDashboardLayoutCatalog(
      dashboardRole: string
    ): Promise<ApiResponse<TenantDashboardLayoutCatalogResponse>> {
      const qs = new URLSearchParams();
      if (dashboardRole.trim()) qs.set('dashboard_role', dashboardRole.trim());
      const query = qs.toString();
      const path = query
        ? `/dashboard/tenant-layout/catalog?${query}`
        : '/dashboard/tenant-layout/catalog';
      return client.request<TenantDashboardLayoutCatalogResponse>(path);
    },

    async saveTenantDashboardLayout(
      body: SaveTenantDashboardLayoutRequest
    ): Promise<ApiResponse<TenantDashboardLayoutCatalogResponse>> {
      return client.request<TenantDashboardLayoutCatalogResponse>('/dashboard/tenant-layout', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async resetTenantDashboardLayout(
      dashboardRole: string
    ): Promise<ApiResponse<TenantDashboardLayoutCatalogResponse>> {
      const qs = new URLSearchParams();
      if (dashboardRole.trim()) qs.set('dashboard_role', dashboardRole.trim());
      const query = qs.toString();
      const path = query ? `/dashboard/tenant-layout?${query}` : '/dashboard/tenant-layout';
      return client.request<TenantDashboardLayoutCatalogResponse>(path, { method: 'DELETE' });
    },
  };
}
